import type { Task } from '@obv/api-client';
import {
  EVERYONE, PRAYERS, addDays, countdownLabel, formatTime, groupTasks, habitAppliesOn, longDate, prayerName, salatSchedule, streak, streakLabel,
  weekStartIndex, weekdayOf, type SalatRow,
} from '@obv/core';
import { personColor } from '@obv/ui';
import { useLocation } from 'wouter';
import { newMutationKey, usePrayerTimes } from '@/queries/hooks';
import { useSetHabitLog, useUpdateTask } from '@/queries/chores';
import { useChoreData, useHabitData } from '../choreData';
import { memberLabel, useDisplay } from '../context';
import { Card, CheckCircle, Unavailable } from '../ui';

function WidgetHead({ title, link, onLink }: { title: string; link: string; onLink: () => void }) {
  return (
    <div className="flex flex-none items-baseline justify-between">
      <h2 className="text-[17px] font-normal">{title}</h2>
      <button type="button" className="tap -my-3 h-[44px] text-[13px] font-medium" onClick={onLink}>
        {link}
      </button>
    </div>
  );
}

/** Overdue + today's open chores, tick to complete; done ones sink to the bottom. */
export function ChoresWidget() {
  const ctx = useDisplay();
  const [, navigate] = useLocation();
  const { tasks, completedOn } = useChoreData();
  const update = useUpdateTask();
  const today = ctx.clock.dateKey;
  const g = tasks.data ? groupTasks(tasks.data.items, today, EVERYONE, completedOn) : null;
  const open = g ? [...g.overdue, ...g.today] : [];
  const rows: Task[] = g ? [...open, ...g.doneToday] : [];
  return (
    <Card tone={6} className="flex flex-col overflow-hidden px-5 pb-2 pt-[18px]">
      <WidgetHead title="Chores today" link={open.length ? `${open.length} left ↗` : 'Chores ↗'} onLink={() => navigate('/display/tasks')} />
      <div className="scroll-y mt-1 min-h-0 flex-1">
        {!g ? (
          <Unavailable>Loading chores…</Unavailable>
        ) : rows.length === 0 ? (
          <Unavailable>Nothing due today.</Unavailable>
        ) : (
          rows.map((t) => {
            const who = memberLabel(ctx, t.member_id);
            const overdue = !t.completed && t.due_date !== null && t.due_date < today;
            return (
              <div key={t.id} className="flex min-h-[44px] items-center border-b" style={{ borderColor: 'var(--line2)', opacity: t.completed ? 0.5 : 1 }}>
                <CheckCircle
                  checked={t.completed}
                  label={`${t.title}${t.completed ? ', done' : ''}`}
                  color={personColor(who.colorKey)}
                  disabled={t.id.indexOf('tmp-') === 0}
                  onClick={() => update.mutate({ id: t.id, body: { completed: !t.completed }, key: newMutationKey() })}
                />
                <span className="ellipsis ml-1 min-w-0 flex-1 text-[14px]" style={{ textDecoration: t.completed ? 'line-through' : 'none' }}>
                  {t.title}
                </span>
                <span className="ml-2 flex-none text-[12px]" style={{ color: overdue ? 'var(--now)' : 'var(--muted2)' }}>
                  {overdue ? 'Late' : who.name}
                </span>
              </div>
            );
          })
        )}
      </div>
    </Card>
  );
}

/** Habits that apply today, with a check and the current streak. */
export function HabitsWidget() {
  const ctx = useDisplay();
  const [, navigate] = useLocation();
  const { habits, logs } = useHabitData();
  const setLog = useSetHabitLog();
  const today = ctx.clock.dateKey;
  const wsi = weekStartIndex(ctx.prefs.week_starts);
  const list = habits.data ? habits.data.filter((h) => !h.archived && habitAppliesOn(h.schedule, today)) : [];
  const done: Record<string, boolean> = {};
  for (const l of logs.data || []) if (l.date === today && l.done) done[l.habit_id] = true;
  const left = list.filter((h) => !done[h.id] && h.schedule !== 'weekly').length;
  return (
    <Card tone={7} className="flex flex-col overflow-hidden px-5 pb-2 pt-[18px]">
      <WidgetHead title="Habits today" link={left ? `${left} to go ↗` : 'Habits ↗'} onLink={() => navigate('/display/habits')} />
      <div className="scroll-y mt-1 min-h-0 flex-1">
        {!habits.data ? (
          <Unavailable>Loading habits…</Unavailable>
        ) : list.length === 0 ? (
          <Unavailable>No habits for today.</Unavailable>
        ) : (
          list.map((h) => {
            const who = memberLabel(ctx, h.member_id);
            const color = h.color_key ? personColor(h.color_key) : personColor(who.colorKey);
            return (
              <div key={h.id} className="flex min-h-[44px] items-center border-b" style={{ borderColor: 'var(--line2)' }}>
                <CheckCircle checked={!!done[h.id]} label={`${h.name} today`} color={color} onClick={() => setLog.mutate({ habitId: h.id, date: today, done: !done[h.id] })} />
                <span className="ellipsis ml-1 min-w-0 flex-1 text-[14px]">{h.name}</span>
                <span className="ml-2 flex-none text-[12px] text-muted2">{streakLabel(h, streak(h, logs.data || [], today, wsi))}</span>
              </div>
            );
          })
        )}
      </div>
    </Card>
  );
}

// ---- Prayer widgets ------------------------------------------------------------------------
// Four variants over the same server-fetched data (Aladhan via the API), so a home layout can mix
// them: the full card, just the next prayer, a compact list, and the Hijri date.

export function usePrayerData() {
  const ctx = useDisplay();
  const q = usePrayerTimes(ctx.clock.dateKey);
  const p = q.data;
  const pending = q.isLoading || (!!q.error && q.error.code === 'prayer.pending');
  const isFriday = weekdayOf(ctx.clock.dateKey) === 5;
  const schedule = p ? salatSchedule(p.today, p.tomorrow, ctx.clock.hhmm) : null;
  return { ctx, p, pending, isFriday, schedule };
}

function PrayerPlaceholder({ title, pending, tone }: { title: string; pending: boolean; tone: number }) {
  return (
    <Card tone={tone} className="flex flex-col px-5 py-[18px]">
      <h2 className="text-[17px] font-normal">{title}</h2>
      <Unavailable>{pending ? 'Fetching prayer times…' : 'Prayer times unavailable right now.'}</Unavailable>
    </Card>
  );
}

/** Today's six rows: past dimmed, current tinted with "now", next in bold. */
function PrayerRows({ rows, isFriday, h24, narrow }: { rows: SalatRow[]; isFriday: boolean; h24: boolean; narrow: boolean }) {
  return (
    <div className="mt-1 flex min-h-0 flex-1 flex-col justify-around">
      {rows.map((r) => {
        const isSunrise = r.key === 'sunrise';
        const strong = r.state === 'next' || r.state === 'current';
        return (
          <div
            key={r.key}
            className="flex items-center justify-between rounded-[8px] px-2"
            style={{
              minHeight: narrow ? 22 : 30,
              background: r.state === 'current' ? 'var(--soft)' : 'transparent',
              opacity: r.state === 'past' ? 0.55 : isSunrise ? 0.75 : 1,
            }}
            aria-current={r.state === 'current' ? 'time' : undefined}
          >
            <span className="text-[14px]" style={{ fontWeight: strong ? 600 : 400, fontStyle: isSunrise ? 'italic' : 'normal' }}>
              {prayerName(r.key, isFriday)}
              {r.state === 'current' ? <span className="ml-2 text-[11px] font-normal uppercase tracking-[0.08em] text-muted">now</span> : null}
            </span>
            <span className="tabular text-[14px]" style={{ fontWeight: strong ? 600 : 400 }}>
              {formatTime(r.time, h24)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Full card: Hijri date, a "Next" box with countdown, and today's times. */
export function SalatWidget({ narrow = false }: { narrow?: boolean }) {
  const { ctx, p, pending, isFriday, schedule: s } = usePrayerData();
  if (!p || !s) return <PrayerPlaceholder title="Salat times" pending={pending} tone={8} />;
  const nextName = prayerName(s.next.key, isFriday && !s.next.tomorrow);
  return (
    <Card tone={8} className="flex flex-col overflow-hidden px-5 pb-3 pt-[18px]">
      <div className="flex-none">
        <h2 className="whitespace-nowrap text-[17px] font-normal">Salat times</h2>
        <div className="ellipsis mt-[2px] text-[12px] text-muted">{p.hijri.label}</div>
      </div>
      <div className="mt-2 flex-none rounded-[12px] px-3 py-2" style={{ background: 'var(--soft)', boxShadow: 'inset 0 0 0 1px var(--softBd)' }}>
        <div className="text-[12px] uppercase tracking-[0.08em] text-muted">Next{s.next.tomorrow ? ' · tomorrow' : ''}</div>
        <div className="mt-[2px] flex items-baseline justify-between">
          <span className="font-semibold" style={{ fontSize: narrow ? 18 : 22 }}>
            {nextName} {formatTime(s.next.time, ctx.prefs.h24)}
          </span>
          <span className="ml-2 text-[13px] text-muted2">{countdownLabel(s.next.minutesUntil)}</span>
        </div>
      </div>
      <PrayerRows rows={s.rows} isFriday={isFriday} h24={ctx.prefs.h24} narrow={narrow} />
    </Card>
  );
}

/** Only the next prayer, big enough to read across the room, with the one after it below. */
export function NextPrayerWidget({ narrow = false }: { narrow?: boolean }) {
  const { ctx, p, pending, isFriday, schedule: s } = usePrayerData();
  if (!p || !s) return <PrayerPlaceholder title="Next prayer" pending={pending} tone={2} />;
  const name = prayerName(s.next.key, isFriday && !s.next.tomorrow);
  // The prayer after the next one ("then Isha 18:13"). It falls tomorrow when the next prayer is
  // already tomorrow's Fajr, or when the next prayer is Isha.
  const i = PRAYERS.indexOf(s.next.key);
  const thenKey = PRAYERS[(i + 1) % PRAYERS.length]!;
  const thenTomorrow = s.next.tomorrow || i + 1 >= PRAYERS.length;
  const thenTime = thenTomorrow ? p.tomorrow[thenKey] : p.today[thenKey];
  const thenFriday = weekdayOf(thenTomorrow ? addDays(ctx.clock.dateKey, 1) : ctx.clock.dateKey) === 5;
  return (
    <Card tone={2} className="flex flex-col overflow-hidden px-6 py-5">
      <div className="flex flex-none items-baseline justify-between">
        <div className="text-[12px] font-semibold uppercase tracking-eyebrow text-muted">Next prayer{s.next.tomorrow ? ' · tomorrow' : ''}</div>
        <div className="ellipsis ml-3 text-[12px] text-muted">{p.hijri.label}</div>
      </div>
      <div className="flex min-h-0 flex-1 flex-col justify-center">
        <div className="font-semibold leading-none" style={{ fontSize: narrow ? 30 : 40 }}>
          {name}
        </div>
        <div className="tabular mt-2 font-semibold leading-none tracking-[-0.03em]" style={{ fontSize: narrow ? 56 : 84 }}>
          {formatTime(s.next.time, ctx.prefs.h24)}
        </div>
        <div className="mt-3 text-[18px] text-muted2">{countdownLabel(s.next.minutesUntil)}</div>
      </div>
      <div className="flex flex-none items-baseline justify-between border-t border-line pt-3 text-[14px]">
        <span className="text-muted">Then</span>
        <span>
          {prayerName(thenKey, thenFriday)} <span className="tabular font-semibold">{formatTime(thenTime, ctx.prefs.h24)}</span>
        </span>
      </div>
    </Card>
  );
}

/** Compact list of today's times, without the next-prayer box. */
export function PrayerListWidget({ narrow = false }: { narrow?: boolean }) {
  const { ctx, p, pending, isFriday, schedule: s } = usePrayerData();
  if (!p || !s) return <PrayerPlaceholder title="Prayer times" pending={pending} tone={6} />;
  return (
    <Card tone={6} className="flex flex-col overflow-hidden px-5 pb-3 pt-[18px]">
      <div className="flex flex-none items-baseline justify-between">
        <h2 className="whitespace-nowrap text-[17px] font-normal">Prayer times</h2>
        <span className="ml-2 text-[12px] text-muted2">
          {prayerName(s.next.key, isFriday && !s.next.tomorrow)} {countdownLabel(s.next.minutesUntil)}
        </span>
      </div>
      <PrayerRows rows={s.rows} isFriday={isFriday} h24={ctx.prefs.h24} narrow={narrow} />
    </Card>
  );
}

/** Today's Hijri date, large, with the Gregorian date and any Islamic holiday. */
export function HijriWidget({ narrow = false }: { narrow?: boolean }) {
  const { ctx, p, pending } = usePrayerData();
  if (!p) return <PrayerPlaceholder title="Hijri date" pending={pending} tone={4} />;
  const h = p.hijri;
  return (
    <Card tone={4} className="flex flex-col overflow-hidden px-6 py-5">
      <div className="flex-none text-[12px] font-semibold uppercase tracking-eyebrow text-muted">Hijri date</div>
      <div className="flex min-h-0 flex-1 flex-col justify-center">
        <div className="tabular font-semibold leading-none" style={{ fontSize: narrow ? 64 : 88 }}>
          {h.day}
        </div>
        <div className="mt-2 font-serif leading-tight" style={{ fontSize: narrow ? 22 : 28 }}>
          {h.month}
        </div>
        <div className="mt-1 text-[16px] text-muted2">{h.year} AH</div>
      </div>
      {h.holidays.length ? (
        <div className="flex flex-none flex-wrap">
          {h.holidays.map((x) => (
            <span key={x} className="mb-1 mr-2 rounded-full px-3 py-1 text-[12px]" style={{ background: 'var(--soft)' }}>
              {x}
            </span>
          ))}
        </div>
      ) : null}
      <div className="flex-none border-t border-line pt-3 text-[14px] text-muted">{longDate(ctx.clock.dateKey)}</div>
    </Card>
  );
}

// Palettes that define --salatArt (the "Our family" backgrounds) draw a mosque behind the card.
const SALAT_ART = { background: 'var(--salatArt, none) left bottom / 52% auto no-repeat, var(--c8, var(--card))' };

/** Next prayer large on the left, every prayer on the right (the dashboard's salat card). */
export function SalatSplitWidget({ narrow = false }: { narrow?: boolean }) {
  const { ctx, p, pending, isFriday, schedule: s } = usePrayerData();
  if (!p || !s) return <PrayerPlaceholder title="Salat times" pending={pending} tone={8} />;
  const name = prayerName(s.next.key, isFriday && !s.next.tomorrow);
  if (narrow) {
    // Too narrow to split side by side: the next prayer as a compact line above the list.
    return (
      <Card tone={8} className="flex flex-col overflow-hidden px-4 pb-3 pt-4">
        <div className="flex-none text-[12px] font-semibold uppercase tracking-eyebrow text-muted">Next prayer{s.next.tomorrow ? ' · tomorrow' : ''}</div>
        <div className="mt-1 flex flex-none items-baseline justify-between">
          <span className="ellipsis text-[22px] font-semibold">
            {name} <span className="tabular">{formatTime(s.next.time, ctx.prefs.h24)}</span>
          </span>
        </div>
        <div className="flex-none text-[13px] text-muted2">{countdownLabel(s.next.minutesUntil)}</div>
        <PrayerRows rows={s.rows} isFriday={isFriday} h24={ctx.prefs.h24} narrow />
        <div className="ellipsis mt-1 flex-none text-[11px] text-muted">{p.hijri.label}</div>
      </Card>
    );
  }
  return (
    <Card tone={8} className="flex overflow-hidden" style={SALAT_ART}>
      <div className="flex min-w-0 flex-col py-5 pl-6 pr-4" style={{ flex: '1 1 50%' }}>
        <div className="text-[12px] font-semibold uppercase tracking-eyebrow" style={{ color: 'var(--accentInk, var(--muted))' }}>
          Next prayer{s.next.tomorrow ? ' · tomorrow' : ''}
        </div>
        <div className="flex min-h-0 flex-1 flex-col justify-center" style={{ color: 'var(--accentInk, inherit)', fontFamily: 'var(--clockFont, inherit)' }}>
          <div className="font-semibold leading-none" style={{ fontSize: 40 }}>
            {name}
          </div>
          <div className="tabular mt-2 font-semibold leading-none tracking-[-0.03em]" style={{ fontSize: 68 }}>
            {formatTime(s.next.time, ctx.prefs.h24)}
          </div>
          <div className="mt-3" style={{ fontSize: 18 }}>
            {countdownLabel(s.next.minutesUntil)}
          </div>
        </div>
        <div className="ellipsis flex-none text-[12px] text-muted">{p.hijri.label}</div>
      </div>
      <div className="flex min-w-0 flex-col border-l border-line py-4 pl-3 pr-4" style={{ flex: '1 1 50%' }}>
        <PrayerRows rows={s.rows} isFriday={isFriday} h24={ctx.prefs.h24} narrow={false} />
      </div>
    </Card>
  );
}
